import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, LinearProgress, Paper, Stack,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { api, ApiError } from '../api/client';
import { themeName } from '../copy/mission';
import type { PlanRequest, RoutePlan, RouteTheme } from '../api/types';

/**
 * Deliberately a local copy rather than a shared import.
 *
 * ItineraryPanel has its own, unexported. Hoisting it to util/time would be
 * the tidier answer, but it would touch a working panel to no benefit here —
 * this is three lines and this component is the only other caller.
 */
function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h} hr${m ? ` ${m} min` : ''}` : `${m} min`;
}

/**
 * Does this emphasis change the ROAD, or only the stops?
 *
 * Every emphasis changes which places a day visits. Only some say anything
 * about how it gets between them: the router's own preferences, the
 * characteristics we measure ourselves, or the numbers that weigh them. One
 * that says none of these will draw its stops onto the same line the plan
 * already uses, and offering it here as another PATH is a promise it cannot
 * keep — nine of them offered was nine invitations to build a route and find
 * it identical.
 */
function shapesTheRoad(theme: RouteTheme): boolean {
  return Object.keys(theme.routingOptions ?? {}).length > 0
    || Object.keys(theme.routeCharacteristics ?? {}).length > 0
    || Object.keys(theme.pathOptions ?? {}).length > 0;
}

/**
 * What it changes about the road, in words rather than weights.
 *
 * The card already carries a description, which says what the emphasis is
 * FOR. This says what it does differently to get there, so the reason two
 * routes diverge is on screen beside them rather than in a settings table.
 */
function roadSummary(theme: RouteTheme): string[] {
  const said: string[] = [];
  const road = theme.routingOptions ?? {};
  const strength = (value: number, avoids: string, prefers: string) =>
    (value <= 0.25 ? avoids : value >= 0.75 ? prefers : null);

  const named: [number | undefined, string, string][] = [
    /*
     * Main roads, not motorways.
     *
     * The control that used to read "Motorways" is gone: main roads is
     * motorway, trunk and primary together, and the answer reaches the router
     * as the motorway weight as well. Saying "prefers motorways" beside a
     * control nobody can find, for an answer that covers three classes rather
     * than one, named the wrong thing twice over.
     */
    [road.use_main_roads, 'avoids main roads', 'prefers main roads'],
    [road.use_residential, 'avoids back streets', 'prefers residential streets'],
    [road.use_tolls, 'avoids tolls', 'takes tolls'],
    [road.use_ferry, 'avoids ferries', 'takes ferries'],
  ];
  for (const [value, avoids, prefers] of named) {
    if (value === undefined) continue;
    const word = strength(value, avoids, prefers);
    if (word) said.push(word);
  }
  if (road.shortest) said.push('shortest, not quickest');

  const wants = Object.entries(theme.routeCharacteristics ?? {})
    .filter(([, weight]) => (weight ?? 0) > 0)
    .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0))
    .map(([name]) => (name === 'safety' ? 'near help' : name));
  if (wants.length > 0) said.push(`chooses for ${wants.join(' then ')}`);

  const extra = theme.pathOptions?.extraPct;
  if (extra !== undefined) said.push(`may run ${extra}% longer for it`);

  return said;
}

/**
 * How much of one route's road the other also uses, as a percentage.
 *
 * Two emphases can honestly draw the same line — where the roads are
 * equivalent a preference has nothing to prefer — and a traveller looking at
 * two routes of the same length has no way to tell whether they differ. The
 * shape points are already on screen, so the answer costs nothing to give.
 */
function roadInCommon(a: RoutePlan, b: RoutePlan): number {
  const points = (plan: RoutePlan) => new Set(
    plan.legs.flatMap((leg) => leg.geometry.map(
      ([lng, lat]) => `${lng.toFixed(5)},${lat.toFixed(5)}`,
    )),
  );
  const mine = points(b);
  if (mine.size === 0) return 0;
  const theirs = points(a);
  let shared = 0;
  mine.forEach((point) => { if (theirs.has(point)) shared += 1; });
  return Math.round((100 * shared) / mine.size);
}

const sameSet = (a: string[] = [], b: string[] = []): boolean =>
  a.length === b.length && [...a].sort().join('\u0000') === [...b].sort().join('\u0000');

const sameNumbers = (
  a: Record<string, number> = {},
  b: Record<string, number> = {},
): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) => a[key] === b[key]);
};

/**
 * Would these two emphases plan the same DAY, differing only in the road?
 *
 * The panel offers other ways round a journey, not other journeys. Two
 * emphases drawing from different kinds of site, or holding different
 * distances from different things, produce different stops — and two routes
 * that visit different places are trivially different lines, which tells an
 * operator nothing about either emphasis. Comparable means: the same sites to
 * draw from, the same things kept clear of, at the same distances. What is
 * left to differ is the road, which is the whole question this panel asks.
 */
function comparable(a: RouteTheme, b: RouteTheme): boolean {
  return sameSet(a.categoryKeys, b.categoryKeys)
    && sameSet(a.avoidCategoryKeys, b.avoidCategoryKeys)
    && (a.avoidDistanceM ?? null) === (b.avoidDistanceM ?? null)
    && sameNumbers(a.avoidDistanceByCategory, b.avoidDistanceByCategory);
}

/** The stops a plan actually visits, in the order it visits them. */
function stopIdsOf(plan: RoutePlan): string[] {
  return plan.stops
    .filter((stop) => stop.kind === 'visit' && stop.poiId)
    .map((stop) => stop.poiId as string);
}

/** Every shape point of a plan, as one line the router can be told to avoid. */
function lineOf(plan: RoutePlan): [number, number][] {
  return plan.legs.flatMap((leg) => leg.geometry);
}

/** What one emphasis came back as, once somebody asked for it. */
interface Attempt {
  loading: boolean;
  plan: RoutePlan | null;
  error: string | null;
}

/**
 * The same journey under a different emphasis, one at a time.
 *
 * A route style shapes suggestions softly — categories feed the ranking and
 * the optimiser then does as it likes, so "food-led" can still return four
 * museums. An emphasis reserves its share of the shortlist before the
 * optimiser runs, which is what makes an alternative genuinely a different
 * day rather than a shuffle of the same one.
 *
 * Every emphasis on offer is LISTED as soon as there is a plan to compare
 * against, and none of them is planned until it is asked for. Planning them
 * all took a full pipeline run each — the matrix, the solve, the routing,
 * and the provider lookups behind them — to fill a list somebody then took
 * one line from. What they cost is what they cost; the difference is that
 * now it is spent on the one that was wanted.
 */
export function ThemedAlternatives({
  draft,
  current,
  hasStyle,
  onApply,
  onRestore,
  appliedKey = null,
}: {
  /** The request the current plan came from, replayed under one emphasis. */
  draft: PlanRequest | null;
  /** The route on screen, to say how far each alternative departs from it. */
  current: RoutePlan | null;
  /**
   * A route style is already shaping this day.
   *
   * Emphases and styles answer the same question, so offering both invites a
   * traveller to set two overlapping controls and wonder which won. The
   * style is the more specific choice and keeps precedence.
   */
  hasStyle: boolean;
  onApply: (themeKey: string, plan: RoutePlan) => void;
  /**
   * Put the route that was on screen first back on it.
   *
   * Absent until an alternative has actually replaced something, so the
   * button appears when there is somewhere to go back TO and not before.
   */
  onRestore?: (() => void) | undefined;
  /** Which emphasis is currently drawn on the map, so the list can say so. */
  appliedKey?: string | null;
}) {
  const [themes, setThemes] = useState<RouteTheme[]>([]);
  const [attempts, setAttempts] = useState<Record<string, Attempt>>({});
  /*
   * Shut until somebody opens it.
   *
   * Seven emphases at a card apiece is a screen and a half of alternatives
   * standing above the itinerary they are alternatives TO — read past every
   * time by anybody who is happy with the route they have. Closed, it is one
   * line that says the alternatives exist.
   */
  const [open, setOpen] = useState(false);
  /*
   * Two different questions, and only the operator knows which is being
   * asked.
   *
   * "Same stops" holds the day exactly as planned and asks each emphasis for
   * another ROAD between them — the comparison that isolates the emphasis's
   * road preferences, and the one that needs the route already on screen
   * taken away or two emphases can honestly return the same line. "Its own
   * stops" is the older behaviour: a different day entirely, chosen by what
   * that emphasis is for.
   */
  const [sameStops, setSameStops] = useState(true);

  useEffect(() => {
    void api.themes().then(setThemes).catch(() => setThemes([]));
  }, []);

  /*
   * Cleared whenever the request changes.
   *
   * A route planned for a different start, mode or time budget is not an
   * alternative to what is on screen — offering it would invite applying a
   * route to a journey it was never planned for.
   */
  useEffect(() => {
    setAttempts({});
  }, [draft]);

  /*
   * And whenever the question changes.
   *
   * A route built for its own stops is not an answer to "the same stops by
   * another road", and leaving it on screen under the new heading would
   * label it as one.
   */
  useEffect(() => {
    setAttempts({});
  }, [sameStops]);

  /*
   * Comparable to the emphasis actually chosen, and never that one itself.
   *
   * Three conditions, and all three earn their place. It must draw from the
   * same sites and hold the same exclusions, or the routes differ because
   * they visit different places and the comparison says nothing about roads.
   * It must say something about roads itself, or it will draw those stops
   * onto the same line. And it must not be the emphasis already on screen,
   * which would offer to build what is already built.
   *
   * Nothing comparable means no panel at all. An emphasis with no peer has
   * no other way round to offer, and a heading over an empty list is a
   * question with no answer.
   */
  /*
   * The emphasis on the FORM, not the one previewed on the map.
   *
   * Applying an alternative does not change what the day was planned as, and
   * taking the applied key here would quietly re-root the comparison at the
   * preview: the list would offer that one's peers, and the way back would
   * offer to return to the route already showing.
   */
  const chosen = themes.find((theme) => theme.key === draft?.themeKey) ?? null;
  const roadThemes = chosen
    ? themes.filter((theme) => theme.key !== chosen.key
      && shapesTheRoad(theme)
      && comparable(theme, chosen))
    : [];
  if (!chosen || roadThemes.length === 0 || !draft) return null;

  if (hasStyle) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
          <AutoAwesomeIcon fontSize="small" color="primary" />
          <Typography variant="h3">Other emphases</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          A route style is already shaping this day. Clear it to compare
          {' '}{roadThemes.map((t) => themeName(t.key, t.label)).join(', ')} versions.
        </Typography>
      </Paper>
    );
  }

  const run = async (theme: RouteTheme) => {
    setAttempts((current) => ({
      ...current,
      [theme.key]: { loading: true, plan: null, error: null },
    }));
    const stops = current ? stopIdsOf(current) : [];
    /*
     * The candidate emphasis's road answers, not the chosen one's.
     *
     * `draft` carries what the form is holding, and on an untouched form those
     * ARE the chosen emphasis's own answers — which the server then lets the
     * REQUEST override the candidate emphasis's with, because the specific
     * answer beats the general one. Replaying the draft under a new themeKey
     * therefore asked every alternative to drive the chosen emphasis's roads,
     * and the one panel whose whole purpose is to show another way round drew
     * the same line under every name on it.
     *
     * Swapped per key on the rule the form already uses: a value still
     * standing as the chosen emphasis left it is that emphasis speaking and is
     * taken back out; one that differs was answered for THIS journey by hand
     * and stays, because a request beating an emphasis is the rule everywhere
     * else and this panel is not the place to make an exception to it.
     */
    const swap = <T,>(
      held: Record<string, T>,
      was: Record<string, T>,
      now: Record<string, T>,
    ): Record<string, T> => {
      const out: Record<string, T> = { ...held };
      for (const [key, value] of Object.entries(was)) {
        if (out[key] === value) delete out[key];
      }

      return { ...out, ...now };
    };
    const asRecord = <T,>(value: Record<string, T> | undefined) => ({ ...(value ?? {}) });
    const roadAnswers = swap(
      asRecord(draft.routingOptions),
      asRecord(chosen.routingOptions),
      asRecord(theme.routingOptions),
    );
    const wanted = swap(
      asRecord(draft.routeCharacteristics),
      asRecord(chosen.routeCharacteristics),
      asRecord(theme.routeCharacteristics),
    );
    const weights = swap(
      asRecord(draft.pathOptions),
      asRecord(chosen.pathOptions),
      asRecord(theme.pathOptions),
    );
    /*
     * Empty stays absent, on the same grounds the form sends nothing: an empty
     * object is a request saying "no preferences", where the truth is that
     * neither emphasis nor traveller answered, and the emphasis's own answers
     * would then have nothing left to merge with.
     */
    const roads: Partial<PlanRequest> = {
      ...(Object.keys(roadAnswers).length > 0 ? { routingOptions: roadAnswers } : {}),
      ...(Object.keys(wanted).length > 0 ? { routeCharacteristics: wanted } : {}),
      ...(Object.keys(weights).length > 0 ? { pathOptions: weights } : {}),
    };
    /*
     * Holding the stops takes three things, not one.
     *
     * The places, so the same ones are visited; the order, so the day is not
     * re-solved into a different shape; and the line already drawn, so the
     * router has to find another road rather than returning the one it just
     * returned. Without the third, two emphases over identical stops
     * legitimately draw the same line and the comparison shows nothing.
     */
    const base: PlanRequest = {
      ...draft,
      routingOptions: undefined,
      routeCharacteristics: undefined,
      pathOptions: undefined,
      ...roads,
      themeKey: theme.key,
    };
    const request: PlanRequest = sameStops && current && stops.length > 0
      ? {
        ...base,
        mustVisitPoiIds: stops,
        stopOrder: stops,
        stopOrderLocked: true,
        differentFrom: [lineOf(current)],
      }
      : base;

    try {
      // The one lane: building an emphasis abandons whatever plan was
      // running, including another emphasis somebody clicked a second ago.
      const plan = await api.planLatest(request);
      setAttempts((current) => ({
        ...current,
        [theme.key]: { loading: false, plan, error: null },
      }));
    } catch (err) {
      /*
       * An abandoned build is not a failed one.
       *
       * Clicking a second emphasis cancels the first, and the first should go
       * back to offering itself rather than showing an error nobody caused.
       */
      const abandoned = err instanceof DOMException && err.name === 'AbortError';
      setAttempts((current) => {
        const next = { ...current };
        if (abandoned) delete next[theme.key];
        else next[theme.key] = { loading: false, plan: null, error: (err as ApiError).message };
        return next;
      });
    }
  };

  const built = roadThemes.filter((theme) => attempts[theme.key]?.plan).length;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {/*
        The whole panel opens and shuts from its own heading — the same
        gesture as every other section on this screen, and the reason the
        list can be complete once it is open.
      */}
      <Stack
        direction="row"
        spacing={1}
        onClick={() => setOpen((shown) => !shown)}
        sx={{ alignItems: 'center', cursor: 'pointer' }}
      >
        <AutoAwesomeIcon fontSize="small" color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Other ways round</Typography>
        {built > 0 && !open && (
          <Chip size="small" variant="outlined" label={`${built} built`} />
        )}
        <Typography variant="caption" color="text.secondary">
          {open ? 'Hide' : `${roadThemes.length} available`}
        </Typography>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            color: 'text.secondary',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms',
          }}
        />
      </Stack>

      {/*
        The way back.

        Outside the fold deliberately: somebody who has applied an
        alternative and shut the panel can still see that the map is not
        showing the day they planned, and undo it in one click. Inside, it
        would be a button you have to remember exists.
      */}
      {onRestore && appliedKey && (
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 0.5 }}
        >
          <Chip
            size="small"
            color="primary"
            label={`Showing ${themeName(
              themes.find((t) => t.key === appliedKey)?.key ?? appliedKey,
              themes.find((t) => t.key === appliedKey)?.label ?? appliedKey,
            )}`}
          />
          <Button size="small" onClick={onRestore}>
            Back to {themeName(chosen.key, chosen.label)}
          </Button>
        </Stack>
      )}

      {!open && (
        <Typography variant="caption" color="text.secondary">
          {roadThemes.length === 1
            ? `${themeName(roadThemes[0]!.key, roadThemes[0]!.label)} runs the same `
              + 'day by a different road.'
            : 'The same day, run by a different road.'}
        </Typography>
      )}

      {!open ? null : (
      <>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
        Listed because {themeName(chosen.key, chosen.label)} is not the only
        emphasis that draws from these sites and holds these exclusions — the
        rest differ only in the road. Each says what it does differently, and
        once built, how much of its road is not on the route on screen.
      </Typography>

      {/*
        Which comparison is being made.

        Left alone it is the strict one: the same stops in the same order, by
        another road. That is the question an operator comparing emphases is
        usually asking, and the loose one — let each emphasis pick its own
        day — makes any two routes differ for reasons that have nothing to do
        with roads.
      */}
      <ToggleButtonGroup
        size="small"
        exclusive
        fullWidth
        value={sameStops ? 'same' : 'own'}
        onChange={(_e, value) => { if (value) setSameStops(value === 'same'); }}
        sx={{ mb: 0.5 }}
      >
        <ToggleButton value="same">Same stops, another road</ToggleButton>
        <ToggleButton value="own">Its own stops</ToggleButton>
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {sameStops
          ? 'Holds this day\u2019s stops and their order, and keeps off the road '
            + 'already drawn, so only the way between them changes.'
          : 'Each emphasis draws its stops from its own kinds of site, so the '
            + 'day itself changes as well as the road.'}
      </Typography>

      <Stack spacing={1}>
        {roadThemes.map((theme) => {
          const attempt = attempts[theme.key];
          const plan = attempt?.plan ?? null;
          const applied = appliedKey === theme.key;
          const outcome = plan?.theme;
          const stops = plan ? plan.stops.length - 2 : 0;

          return (
            /*
              The chosen route is marked rather than merely remembered. A list
              of routes that look identical, one of which is silently on the
              map, is the kind of ambiguity that has people clicking to find
              out.
            */
            <Paper
              key={theme.key}
              variant="outlined"
              sx={{
                p: 1.5,
                ...(applied && {
                  borderColor: 'primary.main',
                  borderWidth: 2,
                  bgcolor: 'action.selected',
                }),
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                  {themeName(theme.key, theme.label)}
                </Typography>
                {applied && <Chip size="small" color="primary" label="On the map" />}
                {/*
                  Before it is planned, the promise. After, what was actually
                  achieved — a themed stop that will not fit the day is dropped
                  like any other, so asserting the target would sometimes be a
                  lie.
                */}
                {plan && stops === 0 ? (
                  /*
                    An emphasis whose stay times are all nought passes its
                    sites rather than stopping at them, so it has no share to
                    report. "0% on theme" is arithmetically true and reads as
                    a failure of the very thing that was asked for.
                  */
                  <Chip size="small" variant="outlined" label="passes by" />
                ) : outcome ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={outcome.met ? 'success' : 'warning'}
                    label={`${Math.round(outcome.share * 100)}% on theme`}
                  />
                ) : theme.categoryKeys.length === 0 ? (
                  // Nothing to visit of its own: this one changes the road
                  // and nothing else.
                  <Chip size="small" variant="outlined" label="road only" />
                ) : (
                  // The promised share is gone: it was never enforced, and a
                  // number on a card that nothing acts on is worse than no
                  // number. What the emphasis collects is said in words below.
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${theme.categoryKeys.length} kinds of site`}
                  />
                )}
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  // Two lines until there is something to read it against.
                  WebkitLineClamp: plan ? 'none' : 2,
                  overflow: 'hidden',
                }}
              >
                {theme.description}
              </Typography>

              {/*
                What it does differently, before it is built. This is the
                whole reason the emphasis is in this list rather than the
                one above it.
              */}
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
              >
                {roadSummary(theme).map((said) => (
                  <Chip key={said} size="small" variant="outlined" label={said} />
                ))}
              </Stack>

              {plan && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {stops} stops · {formatDuration(plan.totals.totalMinutes)} ·{' '}
                  {(plan.totals.distanceMeters / 1000).toFixed(1)} km
                </Typography>
              )}

              {/*
                Whether it actually went another way.

                Said plainly either way: an emphasis that drew the same line
                did so because the ground offered one road, and hiding that
                leaves somebody comparing two identical maps and wondering
                what they are looking for.
              */}
              {plan && current && (() => {
                const common = roadInCommon(current, plan);
                return (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: common >= 95 ? 'text.secondary' : 'primary.main' }}
                  >
                    {common >= 95
                      ? 'Same road as the route on screen — the ground offers no other way.'
                      : `${100 - common}% of this road is not on the route on screen.`}
                  </Typography>
                );
              })()}

              {attempt?.error && (
                <Alert severity="warning" variant="outlined" sx={{ mt: 1, py: 0 }}>
                  {attempt.error}
                </Alert>
              )}

              {attempt?.loading && (
                <LinearProgress sx={{ mt: 1, height: 4, borderRadius: 2 }} />
              )}

              <Box sx={{ mt: 1 }}>
                {plan ? (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant={applied ? 'contained' : 'outlined'}
                      disabled={applied}
                      onClick={() => onApply(theme.key, plan)}
                    >
                      {applied ? 'Showing this route' : 'Use this route'}
                    </Button>
                    <Button size="small" onClick={() => void run(theme)}>Plan again</Button>
                  </Stack>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={attempt?.loading ?? false}
                    onClick={() => void run(theme)}
                    startIcon={
                      attempt?.loading
                        ? <CircularProgress size={14} color="inherit" />
                        : undefined
                    }
                  >
                    {attempt?.loading ? 'Building it…' : 'Build this one'}
                  </Button>
                )}
              </Box>
            </Paper>
          );
        })}
      </Stack>
      </>
      )}
    </Paper>
  );
}
